import type { Heat } from '../../types/heats';

/** Minutes depuis minuit, à partir d'un `HH:MM[:SS]`. */
export const toMinutes = (hhmm?: string): number | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':');
  const hh = parseInt(h, 10);
  const mm = parseInt(m, 10);
  return Number.isFinite(hh) && Number.isFinite(mm) ? hh * 60 + mm : null;
};

/** Écart minimal, en minutes, au-delà de la cadence pour parler de pause. */
const SEUIL_PAUSE = 5;

/** Médiane BASSE d'une liste non vide.
 *
 * Basse et non haute : avec un nombre pair de valeurs, prendre celle du haut
 * laisserait la pause elle-même définir la cadence, et elle s'annulerait. Sur
 * `[12, 22]` la cadence est 12 — et 22 est une pause de 10 — pas l'inverse.
 */
const medianeBasse = (valeurs: number[]): number => {
  const tries = [...valeurs].sort((a, b) => a - b);
  return tries[Math.floor((tries.length - 1) / 2)];
};

/**
 * La nature d'un écart. Trois, parce que le générateur connaît trois pauses
 * (cloud-api `core/heat_spacing.py`) :
 *   - `bloc`      : entre deux heats du même bloc (même WOD, même catégorie) ;
 *   - `categorie` : entre deux blocs du même WOD — le temps de changer les charges ;
 *   - `wod`       : entre deux WODs.
 */
type Nature = 'bloc' | 'categorie' | 'wod';
const NATURES: Nature[] = ['bloc', 'categorie', 'wod'];

const wodDe = (h: Heat): string => (h.wod_id || 'no-wod') as string;

/**
 * Pauses DÉDUITES de l'espacement, sans objet en base.
 *
 * Une pause n'est pas stockée : elle se lit dans les horaires. On établit la
 * cadence habituelle de la journée et tout écart nettement supérieur est une
 * pause, dont la durée est l'excédent.
 *
 * Pourquoi déduire plutôt que persister : une pause stockée devrait être tenue
 * cohérente à chaque réorganisation, déplacement de jour ou regénération — un
 * objet de plus à synchroniser, donc à laisser pourrir. Déduite, elle ne peut
 * pas être périmée.
 *
 * ## Trois cadences, pas une
 *
 * Les écarts d'une journée ne sont pas de même nature. À l'intérieur d'un bloc
 * — même WOD, même catégorie — les heats s'enchaînent au rythme du WOD. Au
 * changement de catégorie, on change les charges. Au changement de WOD, on
 * change le matériel. Le générateur pose une pause différente pour chacune de
 * ces trois situations ; les mélanger fait échouer la détection dans les deux
 * sens. Une cadence unique inventerait une pause à chaque transition d'un
 * événement dont les WODs sont espacés ; et ne raisonner qu'à l'intérieur des
 * blocs rate la vraie pause quand chaque bloc n'a qu'une heat — ce qui est le
 * cas courant, et ce qui masquait la coupure de midi d'un événement réel le
 * 2026-08-16.
 *
 * On compare donc chaque écart à la cadence des écarts de SA nature.
 *
 * ## De la fin d'une heat au début de la suivante
 *
 * Un écart se mesure de la FIN d'une heat au début de la suivante, quand la
 * durée des heats est connue (`time_cap_minutes`, servi par `GET /heats`).
 * Mesuré de départ à départ, l'écart entre deux WODs de 10 et 20 minutes
 * diffère de 10 minutes sans qu'il y ait la moindre pause : sur un événement
 * aux durées variées, chaque transition devenait une fausse « Pause de X min ».
 * Sans durée sur toutes les heats, on retombe sur le départ à départ — la
 * seule mesure disponible — sans mélanger les deux dans une même cadence.
 *
 * ## Garde-fou
 *
 * Il faut au moins deux écarts d'une nature pour qu'elle ait une cadence. Avec
 * un seul, rien ne distingue « espacement normal » de « pause » : on n'affiche
 * alors rien plutôt qu'un chiffre qu'il faudrait excuser.
 *
 * @param blocks suites contiguës de heats, dans l'ordre des heures
 * @returns par id de heat, la pause qui la précède, en minutes
 */
export function derivePauses(blocks: Heat[][]): Record<string, number> {
  const datees = blocks
    .map(bloc => bloc.filter(h => toMinutes(h.start_time) !== null))
    .filter(bloc => bloc.length > 0);

  // La durée ne sert que si TOUTES les heats la portent : un mélange de
  // mesures fin→début et départ→départ n'aurait pas de médiane qui vaille.
  const dureesConnues = datees.every(bloc =>
    bloc.every(h => typeof h.time_cap_minutes === 'number' && h.time_cap_minutes > 0),
  );
  const ecartEntre = (avant: Heat, apres: Heat): number =>
    toMinutes(apres.start_time)! - toMinutes(avant.start_time)!
    - (dureesConnues ? (avant.time_cap_minutes as number) : 0);

  /** Un écart : sa durée, la heat qu'il précède, et sa nature. */
  const ecarts: { duree: number; heatId: string; nature: Nature }[] = [];

  datees.forEach((bloc, b) => {
    for (let i = 1; i < bloc.length; i++) {
      ecarts.push({ duree: ecartEntre(bloc[i - 1], bloc[i]), heatId: bloc[i].id, nature: 'bloc' });
    }
    // Jonction avec le bloc suivant : même WOD → changement de catégorie.
    const suivant = datees[b + 1];
    if (suivant) {
      const derniere = bloc[bloc.length - 1];
      ecarts.push({
        duree: ecartEntre(derniere, suivant[0]),
        heatId: suivant[0].id,
        nature: wodDe(derniere) === wodDe(suivant[0]) ? 'categorie' : 'wod',
      });
    }
  });

  const cadences = new Map<Nature, number>();
  NATURES.forEach(nature => {
    const durees = ecarts.filter(e => e.nature === nature).map(e => e.duree);
    if (durees.length >= 2) {
      const cadence = medianeBasse(durees);
      if (cadence >= 0) cadences.set(nature, cadence);
    }
  });

  const pauses: Record<string, number> = {};
  ecarts.forEach(({ duree, heatId, nature }) => {
    const cadence = cadences.get(nature);
    if (cadence === undefined) return;
    const excedent = duree - cadence;
    if (excedent >= SEUIL_PAUSE) pauses[heatId] = excedent;
  });

  return pauses;
}
